import { type Row, flexRender } from '@tanstack/react-table';
import { ArrowRight } from 'lucide-react';
import { groupedColumns } from '@/components/data-table/data-table-draggable-item';
import { columnWidthStyle } from '@/components/data-table/table-layout';
import { TableCell, TableRow } from '@/components/ui/table';
import { DraggableItems } from '@/types/dragDrop';
import { Badge } from '@/components/ui/badge';
import { type Asset } from '@/types/asset';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const DraggedItem = <TData extends Asset>({
  galleryView,
  overItem,
  row,
  numberOfItems,
}: {
  galleryView: GalleryView;
  overItem: any;
  row: Row<TData>;
  numberOfItems: number;
}) => {
  const tCommon = useTranslations('common');
  const renderedNumberOfItems = numberOfItems > 5 ? 5 : numberOfItems < 2 ? null : numberOfItems;
  const filterCells = row.getVisibleCells().filter((cell) => {
    // If the column definition has an accessorKey directly, use that.
    const accessorKey = (cell.column.columnDef as { accessorKey?: string }).accessorKey;
    // Force include thumbnail even if it's toggled off elsewhere.
    return accessorKey === 'thumbnail' || accessorKey === 'name';
  });

  return (
    <>
      {galleryView === GalleryView.LIST ? (
        <TableRow key={`list-${row.id}`} className="relative border-transparent!" data-cy={`table-row-${row.index}`}>
          {filterCells.map(
            (cell: { id: string; column: { columnDef: any; getSize: () => number }; getContext: () => any }) => {
              return (
                <TableCell
                  key={cell.id}
                  style={columnWidthStyle(cell.column)}
                  className={cn('z-50! select-none bg-ui-light', cell.column.columnDef.meta?.cellClassName)}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            },
          )}
          {renderedNumberOfItems &&
            Array.from({ length: renderedNumberOfItems })?.map((_, index: number) => (
              <div
                key={`gallery-${row.id}-${index}`}
                style={{
                  transform: `translate(${(30 / renderedNumberOfItems) * index}px, ${
                    (30 / renderedNumberOfItems) * index
                  }px) rotate(${(1 / renderedNumberOfItems) * index}deg)`,
                  zIndex: -(index + 1),
                }}
                className="absolute inset-0 size-full bg-ui-light shadow-md"
              />
            ))}
        </TableRow>
      ) : (
        <>
          <div key={`tile-${row.id}`} className="absolute inset-0 z-50 h-full bg-ui-light p-3">
            {Object.entries(groupedColumns).map(([group, { keys, className }]) => (
              <div key={`${group}-${row.id}`} className={className}>
                {row
                  .getVisibleCells()
                  .filter((cell) => keys.includes((cell.column.columnDef as { accessorKey: string }).accessorKey))
                  .map((cell) => flexRender(cell.column.columnDef.cell, cell.getContext()))}
              </div>
            ))}
          </div>
          {renderedNumberOfItems && (
            <>
              {Array.from({ length: renderedNumberOfItems })?.map((_, index: number) => (
                <div
                  key={`gallery-${row.id}-${index}`}
                  style={{
                    transform: `translate(${(30 / renderedNumberOfItems) * index}px, ${
                      (30 / renderedNumberOfItems) * index
                    }px) rotate(${(1 / renderedNumberOfItems) * index}deg)`,
                    zIndex: renderedNumberOfItems - index,
                  }}
                  className="absolute inset-y-0 left-0 size-full bg-ui-light shadow-md"
                />
              ))}
            </>
          )}
        </>
      )}
      {renderedNumberOfItems && (
        <Badge variant="destructive" size="md" className="absolute -top-2 -left-2 z-50 rounded-md px-2">
          {numberOfItems}
        </Badge>
      )}
      {overItem &&
        (overItem.type === DraggableItems.FOLDER ||
          overItem.type === DraggableItems.ALBUM ||
          overItem.type === 'trash') && (
          <div className="absolute -top-4 left-0 z-50 flex max-w-50 min-w-30 translate-x-8 items-center bg-highlight-dark px-1 text-[12px] text-text-inverted dark:bg-highlight-base dark:text-black">
            <ArrowRight className="mr-1 icon-4 shrink-0" />
            <div className="truncate overflow-hidden whitespace-nowrap">
              {overItem.type === DraggableItems.FOLDER || overItem.type === 'trash'
                ? tCommon('dragMoveTo', { name: overItem.name ?? overItem.type })
                : tCommon('dragAddTo', { name: overItem.name ?? overItem.type })}
            </div>
          </div>
        )}
    </>
  );
};
